import { motion } from 'framer-motion'
import { Activity, Heart, Zap, Cloud, ArrowRight, Timer } from 'lucide-react'

export default function Treadmill() {
  const features = [
    { icon: Zap, title: 'AI 教练陪跑', desc: '智能分析体能，推荐最佳训练方案' },
    { icon: Heart, title: '心率监测', desc: '实时心率监测，运动更安全' },
    { icon: Activity, title: '数据可视化', desc: '配速、距离、卡路里实时展示' },
    { icon: Cloud, title: '云端同步', desc: '运动数据自动上传，长期追踪' },
  ]

  return (
    <div className="min-h-screen pt-16">
      {/* Hero */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[500px] opacity-15"
          style={{ background: 'radial-gradient(ellipse, rgba(139,92,246,0.5), transparent 70%)', filter: 'blur(60px)' }} />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium mb-6"
              style={{ background: 'rgba(139,92,246,0.15)', border: '1px solid rgba(139,92,246,0.3)', color: '#a78bfa' }}>
              <div className="w-2 h-2 rounded-full bg-purple-400 animate-pulse" />
              智能硬件
            </div>

            <h1 className="text-4xl md:text-6xl font-black text-white mb-6 tracking-tight">
              Lafit智能跑步机
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-8">
              AI 教练系统 + 实时数据监测，让跑步更科学
            </p>

            <a
              href="https://sh.lafit.club"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-bold text-white transition-all duration-300 hover:gap-3"
              style={{ background: '#8b5cf6', boxShadow: '0 0 30px rgba(139,92,246,0.4)' }}
            >
              了解更多
              <ArrowRight className="w-5 h-5" />
            </a>
          </motion.div>

          {/* Demo */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-16 max-w-lg mx-auto"
          >
            <div className="relative rounded-2xl overflow-hidden"
              style={{
                background: 'linear-gradient(135deg, rgba(15,15,30,0.9), rgba(20,20,50,0.9))',
                border: '1px solid rgba(139,92,246,0.2)',
                boxShadow: '0 0 60px rgba(139,92,246,0.15), 0 40px 80px rgba(0,0,0,0.5)',
              }}
            >
              <div className="flex items-center justify-between px-5 py-3 border-b border-white/5">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-green-400" />
                  <span className="text-xs text-gray-400 font-mono">智能跑步机 · 训练中</span>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <div className="p-4 rounded-xl" style={{ background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.2)' }}>
                  <div className="flex items-end justify-between mb-2">
                    <div>
                      <div className="text-3xl font-bold text-white">5.2</div>
                      <div className="text-xs text-gray-500">km/h</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xl font-bold text-purple-400">325</div>
                      <div className="text-xs text-gray-500">kcal</div>
                    </div>
                  </div>
                  <div className="h-1.5 bg-white/5 rounded-full overflow-hidden mt-3">
                    <div className="h-full rounded-full bg-gradient-to-r from-purple-500 to-purple-400" style={{ width: '65%' }} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 rounded-xl flex items-center gap-2" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <Heart className="w-4 h-4 text-red-400" />
                    <div>
                      <div className="text-sm font-bold text-white">142</div>
                      <div className="text-xs text-gray-500">BPM</div>
                    </div>
                  </div>
                  <div className="p-3 rounded-xl flex items-center gap-2" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <Timer className="w-4 h-4 text-blue-400" />
                    <div>
                      <div className="text-sm font-bold text-white">32:15</div>
                      <div className="text-xs text-gray-500">时长</div>
                    </div>
                  </div>
                </div>
                <div className="p-3 rounded-xl" style={{ background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.15)' }}>
                  <div className="text-xs text-gray-400 mb-1">AI 教练建议</div>
                  <div className="text-sm text-purple-400">当前心率 142，建议维持当前配速</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 relative">
        <div className="absolute inset-0 grid-bg opacity-10" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-black text-white mb-4">核心功能</h2>
            <p className="text-gray-400 max-w-2xl mx-auto">AI 加持，让跑步更科学、更高效</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="p-6 rounded-2xl text-center"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.04), rgba(255,255,255,0.01))',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-4"
                  style={{ background: 'rgba(139,92,246,0.15)', color: '#a78bfa' }}>
                  <feature.icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-sm text-gray-400">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(139,92,246,0.1), rgba(59,130,246,0.1))' }} />
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-black text-white mb-6">
              AI 赋能，让跑步更聪明
            </h2>
            <p className="text-gray-400 mb-8 max-w-xl mx-auto">
              了解更多智能跑步机产品信息
            </p>
            <a
              href="https://sh.lafit.club"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-bold text-white transition-all duration-300 hover:gap-3"
              style={{ background: '#8b5cf6', boxShadow: '0 0 30px rgba(139,92,246,0.4)' }}
            >
              访问官网
              <ArrowRight className="w-5 h-5" />
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  )
}
